export enum AwsAuthMode {
  StaticKey = "static_key",
  AssumeRole = "assume_role",
  InstanceRole = "instance_role",
}

/**
 * Derive the AWS auth mode of an existing run environment.
 * Precedence matches the launch path (getAmazonVariables): the instance role
 * flag wins only when no credential is attached, but on the form we still show
 * InstanceRole whenever the flag is set so the user sees the mode they saved.
 */
export const deriveAwsAuthMode = (params: {
  awsUseInstanceRole: boolean | null | undefined;
  amazonCreId: number | null | undefined;
  assumeRoleCredentialIds: number[];
}): AwsAuthMode => {
  if (params.awsUseInstanceRole) return AwsAuthMode.InstanceRole;
  if (
    params.amazonCreId != null &&
    params.assumeRoleCredentialIds.includes(params.amazonCreId)
  ) {
    return AwsAuthMode.AssumeRole;
  }
  return AwsAuthMode.StaticKey;
};

/**
 * Resolve the AWS auth fields for the saved run-environment payload.
 * Instance-role mode intentionally omits amazonCreId so the server clears it;
 * the launch path honors awsUseInstanceRole only when no credential is attached.
 */
export const resolveAwsAuthPayload = (
  mode: AwsAuthMode,
  selectedAmazonId: number | undefined
): { awsUseInstanceRole: boolean; amazonCreId?: number } => {
  const awsUseInstanceRole = mode === AwsAuthMode.InstanceRole;
  if (!awsUseInstanceRole && selectedAmazonId) {
    return { awsUseInstanceRole, amazonCreId: selectedAmazonId };
  }
  return { awsUseInstanceRole };
};

/**
 * Static-key mode is blocked when the organization requires role-based AWS auth.
 * Used to hide the static-key picker and show the remediation warning.
 */
export const isStaticKeyModeBlocked = (
  mode: AwsAuthMode,
  requireRoleBased: boolean
): boolean => requireRoleBased && mode === AwsAuthMode.StaticKey;
